import { jwtDecode } from "jwt-decode";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { setSentryUser } from "@/v1/sentry";
import { AuthContext } from "./AuthContext";
import { fetchPermissionScope } from "./fetchPermissionScope";
import { tokenStorage } from "./tokenStorage";
import type { Operator } from "./types";
import { useGoogleOneTap } from "./useGoogleOneTap";

interface AuthProviderProps {
  /** React children to be wrapped by the AuthProvider */
  children: ReactNode;
}

// Add an interface for decoded token with expiration
interface DecodedToken extends Operator {
  exp: number; // Expiration timestamp
}

/**
 * AuthProvider component that manages authentication state and provides auth-related
 * functionality to its children through React Context.
 *
 * Features:
 * - Google One Tap authentication
 * - Token persistence in tokenStorage
 * - Permission scope management
 * - Automatic token validation and renewal
 * - Proactive token refresh before expiration
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  // Google One Tap authentication hook
  const { promptOneTap, credentialResponse } = useGoogleOneTap();

  // Authentication state management
  const [operator, setOperator] = useState<Operator | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Refresh timer reference
  const refreshTimerRef = useRef<number | null>(null);

  /**
   * Sets up a timer to refresh the token before it expires
   * Uses 75% of the token's lifetime as the refresh point
   *
   * @param expirationTime - Token expiration timestamp in seconds
   */
  const scheduleTokenRefresh = (expirationTime: number) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expirationTime - currentTime;

    // If token is already expired, logout
    if (timeUntilExpiry <= 0) {
      logout();
      return;
    }

    // Schedule refresh at 75% of remaining lifetime
    const refreshDelay = timeUntilExpiry * 0.75 * 1000;

    refreshTimerRef.current = window.setTimeout(() => {
      promptOneTap();
    }, refreshDelay);
  };

  /**
   * Initializes the authentication state with a given token.
   * This function handles:
   * 1. Token validation
   * 2. Permission scope fetching
   * 3. User data parsing
   * 4. State updates
   * 5. Setting up token refresh
   * 6. Redirecting to the homepage
   *
   * @param token - The authentication token to initialize with
   */
  const initializeAuth = async (token: string) => {
    setLoading(true);
    setError(null);
    try {
      const role = await fetchPermissionScope(token);

      // Parse and set user data with their permission scope
      const userData = jwtDecode<DecodedToken>(token);
      setOperator({ ...userData, permissionScope: role });
      tokenStorage.setToken(token);

      // set sentry user
      setSentryUser({
        id: userData.id,
        email: userData.email,
        name: userData.name,
      });

      // Schedule token refresh based on expiration time
      if (userData.exp) {
        scheduleTokenRefresh(userData.exp);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to parse token");
      tokenStorage.removeToken();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Effect to handle Google One Tap authentication response
   * Initializes auth when credentials are received
   */
  useEffect(() => {
    if (!credentialResponse) return;
    const { credential } = credentialResponse;
    initializeAuth(credential);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialResponse]);

  /**
   * Effect to check for existing authentication token on component mount
   * If a token exists in tokenStorage, it attempts to initialize auth with it
   */
  useEffect(() => {
    const checkExistingToken = async () => {
      try {
        const savedToken = tokenStorage.getToken();
        if (savedToken) {
          // Verify token isn't expired before attempting to use it
          const decoded = jwtDecode<DecodedToken>(savedToken);
          const currentTime = Math.floor(Date.now() / 1000);

          if (decoded.exp && decoded.exp > currentTime) {
            await initializeAuth(savedToken);
          } else {
            // Token is expired, attempt to get a new one
            promptOneTap();
            tokenStorage.removeToken();
          }
        }
      } catch (err) {
        tokenStorage.removeToken();
      } finally {
        setLoading(false);
      }
    };

    checkExistingToken();

    // Cleanup refresh timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Logs out the current user by:
   * 1. Removing the auth token from tokenStorage
   * 2. Clearing the operator state
   * 3. Resetting any error states
   * 4. Clearing any refresh timers
   * 5. Setting a flag to indicate an explicit logout intent
   * 6. Redirecting to the login page
   */
  const logout = () => {
    tokenStorage.removeToken();
    setOperator(null);
    setError(null);

    // Clear any scheduled refresh
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Set a flag to indicate this navigation is due to logout
    sessionStorage.setItem("auth:logoutIntent", "true");

    // Redirect to login page
    navigate("/login");
  };

  return (
    <AuthContext.Provider
      value={{
        login: promptOneTap,
        operator,
        loading,
        error,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
